import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { ApiError, apiFetch } from '../../lib/api';
import {
  buildSupportReportPayload,
  DiagnosticAttachmentError,
  fileToDiagnosticAttachment,
  type DiagnosticAttachmentPayload,
  type DiagnosticSubmitResult,
} from '../../lib/diagnostics';
import { openConcierge } from '../../concierge/conciergePresenter';
import { resolveFaqs, searchFaqs } from './faq';

interface SupportDevice { device_id: string; name?: string; alias?: string; model?: string }

export default function SupportPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [query, setQuery] = useState('');
  const [devices, setDevices] = useState<SupportDevice[]>([]);
  const [deviceId, setDeviceId] = useState('');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [attachments, setAttachments] = useState<DiagnosticAttachmentPayload[]>([]);
  const [feedback, setFeedback] = useState<Record<string, 'yes' | 'no'>>({});
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void apiFetch<SupportDevice[]>('/v3/devices').then((res) => setDevices(res.data)).catch(() => setDevices([]));
  }, []);

  const selected = devices.find((d) => d.device_id === deviceId);
  const faqs = useMemo(() => searchFaqs(resolveFaqs(t), query, selected?.model), [deviceId, devices, query, t]);

  const chooseFiles = useCallback(async (files: File[]) => {
    setError(null);
    if (files.length === 0) return;
    try {
      const next = await Promise.all(files.slice(0, 3).map(fileToDiagnosticAttachment));
      if (files.length > 3) throw new Error(t('support.errorTooManyImages'));
      if (next.reduce((sum, a) => sum + a.byteLength, 0) > 5 * 1024 * 1024) throw new Error(t('support.errorImagesTotal'));
      setAttachments(next);
    } catch (e) {
      setAttachments([]);
      setError(e instanceof DiagnosticAttachmentError ? t('support.errorImageInvalid') : e instanceof Error ? e.message : t('support.errorImageInvalid'));
    }
  }, [t]);

  const submit = useCallback(async () => {
    const cleanSubject = subject.trim();
    const cleanDescription = description.trim();
    if (!cleanSubject || !cleanDescription) {
      setError(t('support.errorRequired'));
      return;
    }
    setBusy(true); setError(null); setMessage(null);
    try {
      const payload = buildSupportReportPayload({ subject: cleanSubject, description: cleanDescription, deviceId, attachments });
      const res = await apiFetch<DiagnosticSubmitResult>('/v3/me/diagnostic-reports', {
        method: 'POST', body: payload,
        idempotencyKey: typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `support-${Date.now()}`,
      });
      setSubject(''); setDescription(''); setAttachments([]); setDeviceId('');
      setMessage(res.data.deduplicated ? t('support.ticketDeduped') : t('support.ticketSent', { id: res.data.report_id }));
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message}（${e.code}）` : t('support.ticketFailed'));
    } finally { setBusy(false); }
  }, [attachments, description, deviceId, subject, t]);

  return (
    <div style={{ maxWidth: 760, display: 'flex', flexDirection: 'column', gap: 'var(--spacing-8)' }}>
      <div><Link to="/account">← {t('account.backToAccount')}</Link><h1>{t('support.title')}</h1><p className="text-secondary">{t('support.subtitle')}</p></div>

      {/*
        客服對話主入口(Home IA §5.1 業主裁決 D69:「帳號 → 說明中心」為恆定主入口,
        位置固定在說明中心頂端、不逐頁漂移)。取代已移除的全域浮動鈕;錨點與 iOS
        `SupportCenterView.swift` 的 support_concierge_entry、Android 同名 testTag 對齊。
      */}
      <section className="card">
        <h2 style={{ marginTop: 0 }}>{t('concierge.title')}</h2>
        <button
          className="btn-primary"
          data-testid="support_concierge_entry"
          onClick={() => openConcierge()}
        >{t('support.conciergeEntry')}</button>
        <p className="text-secondary">{t('support.conciergeHint')}</p>
      </section>

      <section className="card">
        <h2 style={{ marginTop: 0 }}>{t('support.faqTitle')}</h2>
        <input aria-label={t('support.searchLabel')} value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('support.searchPlaceholder')} style={{ width: '100%' }} />
        {faqs.length === 0 && <p className="text-secondary">{t('support.noFaq')}</p>}
        <div className="stack gap-4" style={{ marginTop: 'var(--spacing-4)' }}>
          {faqs.map((faq) => (
            <details key={faq.id} className="card" style={{ padding: 'var(--spacing-4)' }}>
              <summary style={{ cursor: 'pointer', fontWeight: 600 }}>{faq.title}</summary>
              <p style={{ whiteSpace: 'pre-line' }}>{faq.body}</p>
              <div style={{ display: 'flex', gap: 'var(--spacing-3)', alignItems: 'center' }}>
                <span className="text-secondary">{t('support.helpful')}</span>
                <button className="btn-ghost" aria-pressed={feedback[faq.id] === 'yes'} onClick={() => setFeedback((v) => ({ ...v, [faq.id]: 'yes' }))}>{t('support.yes')}</button>
                <button className="btn-ghost" aria-pressed={feedback[faq.id] === 'no'} onClick={() => { setFeedback((v) => ({ ...v, [faq.id]: 'no' })); setSubject(faq.title); setDescription(t('support.notHelpfulPrefill')); }}>{t('support.no')}</button>
              </div>
            </details>
          ))}
        </div>
      </section>

      <section className="card" id="support-ticket">
        <h2 style={{ marginTop: 0 }}>{t('support.ticketTitle')}</h2>
        <p className="text-secondary">{t('support.contactEmail', { email: user?.email ?? '—' })}</p>
        <label className="stack gap-2"><span>{t('support.deviceOptional')}</span><select value={deviceId} onChange={(e) => setDeviceId(e.target.value)}><option value="">{t('support.noDevice')}</option>{devices.map((d) => <option key={d.device_id} value={d.device_id}>{d.alias || d.name || d.model || d.device_id}</option>)}</select></label>
        <label className="stack gap-2" style={{ marginTop: 'var(--spacing-4)' }}><span>{t('support.subject')}</span><input value={subject} maxLength={160} onChange={(e) => setSubject(e.target.value)} /></label>
        <label className="stack gap-2" style={{ marginTop: 'var(--spacing-4)' }}><span>{t('support.description')}</span><textarea value={description} maxLength={4000} onChange={(e) => setDescription(e.target.value)} style={{ minHeight: 120, resize: 'vertical' }} /></label>
        <label className="stack gap-2" style={{ marginTop: 'var(--spacing-4)' }}><span>{t('support.imagesOptional')}</span><input type="file" accept="image/jpeg,image/png,image/webp" multiple disabled={busy} onChange={(e) => { const files = Array.from(e.currentTarget.files ?? []); e.currentTarget.value = ''; void chooseFiles(files); }} /><span className="text-secondary">{t('support.imagesHint')}</span></label>
        {attachments.length > 0 && <ul>{attachments.map((a) => <li key={a.filename}>{a.filename} ({Math.ceil(a.byteLength / 1024)} KB)</li>)}</ul>}
        <p className="text-secondary">{t('support.privacy')}</p>
        <button className="btn-primary" disabled={busy} onClick={() => void submit()}>{busy ? t('support.sending') : t('support.send')}</button>
        {message && <p role="status" style={{ color: 'var(--color-semantic-success)' }}>{message}</p>}
        {error && <p role="alert" className="error-text">{error}</p>}
      </section>
    </div>
  );
}
